import React, { useState, useMemo, useEffect } from "react";
import { FaPlus } from "react-icons/fa";
import TableLoadingRow from "./TableLoadingRow";
import { subscribeToApiLoading } from "../services/httpService";
import {
    Cell,
    Row,
    TableInstance,
    TableState,
    UsePaginationInstanceProps,
    UsePaginationState,
    usePagination,
    useTable,
} from "react-table";

interface TableProps<T extends { _id: string }> {
    columns: any[];
    data: T[];
    pageSize?: number;
    add?: boolean;
    showSearch?: boolean;
    showPagination?: boolean;

    onOpen?: () => void;
    renew?: boolean;
    onOpenRenew?: () => void;
    checkbox?: boolean;
    selectedRowId?: string | null;  // Receive selected row ID as prop
    setSelectedRowId?: React.Dispatch<React.SetStateAction<string | null>>;  // Receive function to set selected row ID
}

type PaginatedTableInstance<T extends object> = TableInstance<T> &
    UsePaginationInstanceProps<T> & {
        state: TableState<T> & UsePaginationState<T>;
    };

const Table = <T extends { _id: string }>({
    columns,
    data,
    pageSize = 5,
    add = false,
    showSearch = true,
    showPagination = true,

    onOpen,
    renew = false,
    onOpenRenew,
    checkbox = false,
    selectedRowId,
    setSelectedRowId,
}: TableProps<T>) => {
    const [searchQuery, setSearchQuery] = useState<string>(""); // State for search query
    const [apiLoading, setApiLoading] = useState(false);

    useEffect(() => subscribeToApiLoading(setApiLoading), []);

    // Memoize filtered data to avoid unnecessary recalculations
    const filteredData = useMemo(() => {
        if (!showSearch) return data;
        return data.filter((row) => {
            return columns.some((column) => {
                const cellValue = row[column.accessor as keyof T];
                return cellValue ? String(cellValue).toLowerCase().includes(searchQuery.toLowerCase()) : false;
            });
        });
    }, [data, searchQuery, columns, showSearch]);  // Only re-filter when data, searchQuery, or columns change

    const effectivePageSize = showPagination ? pageSize : Math.max(filteredData.length, 1);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        setPageSize,
        state: { pageIndex },
    } = useTable<T>({
        columns,
        data: filteredData,  // Pass the filtered data to the table
        initialState: { pageIndex: 0, pageSize: effectivePageSize },
    }, usePagination) as PaginatedTableInstance<T>;

    useEffect(() => {
        if (!showPagination) {
            setPageSize(Math.max(filteredData.length, 1));
        }
    }, [showPagination, filteredData.length, setPageSize]);

    const handleRowSelect = (rowId: string) => {
        if (!setSelectedRowId) return;
        setSelectedRowId((prev) => (prev === rowId ? null : rowId));  // Toggle row selection
    };

    return (
        <div className="w-full overflow-x-auto">
            {/* Search Bar */}
            {showSearch && (
                <div className="mb-4">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}  // Update the search query
                        placeholder="Search..."
                        className="border p-2 rounded-md w-1/4"
                    />
                </div>
            )}

            <div className="flex justify-end mb-2 gap-2">
                {renew && <button onClick={onOpenRenew} className="flex justify-center items-center bg-[#ac120c] w-[100px] h-[30px] text-white rounded-lg">Renew</button>}
                {add && <FaPlus onClick={onOpen} className="text-sm w-[30px] h-[30px] bg-[#ac120c] text-white rounded-lg p-2 cursor-pointer" />}

            </div>

            <table {...getTableProps()} className="w-full border border-gray-300">
                {/* Table Header */}
                <thead className="bg-[#000000] text-white">
                    {headerGroups.map((headerGroup) => (
                        (() => {
                            const headerGroupProps = headerGroup.getHeaderGroupProps();
                            const { key, ...rest } = headerGroupProps as any;
                            return (
                                <tr key={key} {...rest} className="border-b">
                            {checkbox && <th className="p-3 text-left">Select</th>}
                            {headerGroup.headers.map((column) => (
                                (() => {
                                    const headerProps = column.getHeaderProps();
                                    const { key: colKey, ...colRest } = headerProps as any;
                                    return (
                                        <th key={colKey} {...colRest} className="p-3 text-left">
                                            {column.render("Header")}
                                        </th>
                                    );
                                })()
                            ))}
                                </tr>
                            );
                        })()
                    ))}
                </thead>

                {/* Table Body */}
                <tbody {...getTableBodyProps()}>
                    {apiLoading ? (
                        <TableLoadingRow colSpan={columns.length + (checkbox ? 1 : 0)} />
                    ) : page.map((row: Row<T>) => {
                        prepareRow(row);
                        const rowProps = row.getRowProps();
                        const { key: rowKey, ...rowRest } = rowProps as any;
                        return (
                            <tr key={rowKey} {...rowRest} className="border-b hover:bg-gray-100">
                                {checkbox && (
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedRowId === row.original._id}  // Check if row is selected
                                            onChange={() => handleRowSelect(row.original._id)}  // Toggle selection on checkbox click
                                        />
                                    </td>
                                )}
                                {row.cells.map((cell: Cell<T>) => {
                                    const cellProps = cell.getCellProps();
                                    const { key: cellKey, ...cellRest } = cellProps as any;
                                    return (
                                        <td key={cellKey} {...cellRest} className="p-3">
                                            {cell.render("Cell")}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Pagination Controls */}
            {showPagination && (
                <div className="flex justify-between items-center mt-4">
                    <button onClick={previousPage} disabled={!canPreviousPage} className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50">
                        Previous
                    </button>
                    <span>Page {pageIndex + 1}</span>
                    <button onClick={nextPage} disabled={!canNextPage} className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50">
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default Table;
